<script setup>
/**
 * Add or edit one stage of a type's workflow.
 *
 * The two labels sit side by side on purpose: the internal name is what the
 * team works with, and the customer-facing label is the entire customer surface
 * of this engine. Its absence is a feature — a stage with no customer label is
 * silent, which is how a team works without narrating every move — so the field
 * says that rather than reading as an unfinished form.
 *
 * The category is the machine-readable meaning behind whatever the workspace
 * called the stage. Counts and reporting read it, never the name, so it is a
 * fixed picker rather than free text.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import { STAGE_CATEGORY_META } from 'dashboard/helper/supportCaseMeta';

defineProps({
  // Off the type: the labels are saved either way, but nothing is sent when the
  // type does not share progress, and the form says so instead of implying it.
  shareProgressWithCustomer: {
    type: Boolean,
    default: false,
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'close']);

const { t } = useI18n();

const dialogRef = ref(null);
const editing = ref(null);
const name = ref('');
const customerLabel = ref('');
const category = ref('submitted');
// The stage rules (T-13): both optional, and empty means "no rule" — sent as
// null so clearing the field clears the rule rather than leaving it behind.
const wipLimit = ref('');
const slaMinutes = ref('');

const isEditing = computed(() => Boolean(editing.value));
const canSubmit = computed(() => Boolean(name.value.trim()));

const categoryOptions = computed(() =>
  Object.entries(STAGE_CATEGORY_META).map(([value, meta]) => ({
    value,
    label: t(meta.labelKey),
  }))
);

const open = (stage = null) => {
  editing.value = stage;
  name.value = stage?.name || '';
  customerLabel.value = stage?.customerLabel || '';
  category.value = stage?.category || 'submitted';
  wipLimit.value = stage?.wipLimit != null ? String(stage.wipLimit) : '';
  slaMinutes.value = stage?.slaMinutes != null ? String(stage.slaMinutes) : '';
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const onSubmit = () => {
  if (!canSubmit.value) return;
  emit('submit', {
    id: editing.value?.id || null,
    payload: {
      name: name.value.trim(),
      // Empty is how a stage goes silent again, so it is sent rather than
      // omitted: omitting it would leave the old label in place.
      customerLabel: customerLabel.value.trim(),
      category: category.value,
      wipLimit: wipLimit.value === '' ? null : Number(wipLimit.value),
      slaMinutes: slaMinutes.value === '' ? null : Number(slaMinutes.value),
    },
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isEditing
        ? t('SUPPORT_CASES.SETTINGS.STAGES.EDIT_TITLE')
        : t('SUPPORT_CASES.SETTINGS.STAGES.CREATE_TITLE')
    "
    :confirm-button-label="t('SUPPORT_CASES.SETTINGS.FORM.SAVE')"
    :cancel-button-label="t('SUPPORT_CASES.SETTINGS.FORM.CANCEL')"
    :disable-confirm-button="!canSubmit"
    :is-loading="isSubmitting"
    :close-on-click-outside="false"
    @confirm="onSubmit"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4" data-testid="stage-dialog">
      <Input
        v-model="name"
        :label="t('SUPPORT_CASES.SETTINGS.STAGES.INTERNAL_LABEL')"
        :placeholder="t('SUPPORT_CASES.SETTINGS.STAGES.INTERNAL_PLACEHOLDER')"
        data-testid="stage-name"
      />

      <Input
        v-model="customerLabel"
        :label="t('SUPPORT_CASES.SETTINGS.STAGES.CUSTOMER_LABEL')"
        :placeholder="t('SUPPORT_CASES.SETTINGS.STAGES.CUSTOMER_PLACEHOLDER')"
        :message="
          shareProgressWithCustomer
            ? t('SUPPORT_CASES.SETTINGS.STAGES.CUSTOMER_HINT')
            : t('SUPPORT_CASES.SETTINGS.STAGES.CUSTOMER_DISABLED_HINT')
        "
        message-type="info"
        data-testid="stage-customer-label"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_CASES.SETTINGS.STAGES.CATEGORY_LABEL') }}
        </label>
        <Select
          v-model="category"
          :options="categoryOptions"
          data-testid="stage-category"
        />
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('SUPPORT_CASES.SETTINGS.STAGES.CATEGORY_HINT') }}
        </p>
      </div>

      <div class="grid grid-cols-2 gap-4">
        <Input
          v-model="wipLimit"
          type="number"
          min="1"
          :label="t('SUPPORT_CASES.SETTINGS.STAGES.WIP_LIMIT_LABEL')"
          :placeholder="
            t('SUPPORT_CASES.SETTINGS.STAGES.WIP_LIMIT_PLACEHOLDER')
          "
          :message="t('SUPPORT_CASES.SETTINGS.STAGES.WIP_LIMIT_HINT')"
          message-type="info"
          data-testid="stage-wip-limit"
        />

        <Input
          v-model="slaMinutes"
          type="number"
          min="1"
          :label="t('SUPPORT_CASES.SETTINGS.STAGES.SLA_LABEL')"
          :placeholder="t('SUPPORT_CASES.SETTINGS.STAGES.SLA_PLACEHOLDER')"
          :message="t('SUPPORT_CASES.SETTINGS.STAGES.SLA_HINT')"
          message-type="info"
          data-testid="stage-sla-minutes"
        />
      </div>
    </div>
  </Dialog>
</template>
